import { AfterViewInit, Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, filter, takeUntil } from 'rxjs';
import { MenuService } from 'src/app/core/services/menu/menu.service';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { SessionContext } from 'src/app/core/services/config/app.settings';
import { HasDomaineDirective } from '../../../../../shared/directives/has-domaine/has-domaine.directive';
import { HasRoleDirective } from '../../../../../shared/directives/has-role/has-role.directive';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { DomaineCode } from 'src/app/data/intervenant/models/enums/domaine-code.enum';

@Component({
    selector: 'app-menu',
    standalone: true,
    imports: [CommonModule, RouterModule, MenuModule, TranslateModule, HasDomaineDirective, HasRoleDirective, FaIconComponent],
    templateUrl: './menu.component.html',
    styleUrl: './menu.component.scss'
})
export class MenuComponent implements OnInit, OnDestroy, AfterViewInit {
    /**
     * True si l'application est l'ANPP
     */
    @Input() isAnpp: boolean;

    /**
     * Menu
     */
    menu: string;

    /**
     * Items du menu
     */
    items: MenuItem[];

    /**
     * Item actif du menu
     */
    activeItem?: MenuItem;

    /**
     * Item enfant actif du menu
     */
    activeSubItem?: MenuItem;

    /**
     * État d'ouverture du submenu
     */
    submenuOpen = true;

    /**
     * Liste des items du menu
     */
    rawItems: MenuItem[] = [];


    domaine: string;

    /**
     * Router
     */
    private readonly router = inject(Router);

    /**
     * Service de gestion des menus
     */
    private readonly menuService = inject(MenuService);

    /**
     * Service d'authentification
     */
    private readonly authService = inject(AuthService);

    /**
     * Subscription à un observable
     */
    private readonly destroy$ = new Subject<void>();

    private readonly activatedRoute = inject(ActivatedRoute);

    /**
     * On init component
     */
    ngOnInit(): void {
        this.domaine = SessionContext.get('codeDomaine') ?? DomaineCode.VIN;
        this.menu = this.domaine ? `menu.${this.domaine}.` : 'menu.';

        this.rawItems = this.menuService.getRoutes();
        this.items = this.mapItems(this.rawItems);

        this.authService
            .getRoles()
            .pipe(takeUntil(this.destroy$))
            .subscribe(roles => {
                this.items = this.mapItems(this.rawItems);
                this.syncActiveFromUrl();
            });

        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.syncActiveFromUrl();
            });
    }

    /**
     * After view init
     */
    ngAfterViewInit(): void {
        this.syncActiveFromUrl();
    }

    /**
     * On destroy component
     */
    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Map items to menu items
     * @param items - Items to map
     * @private
     */
    private mapItems(items: MenuItem[]): MenuItem[] {
        return (items ?? [])
            .filter(item => !item.hide)
            .filter(item => this.isItemVisible(item))
            .map(item => {
                const children = (item.children ?? [])
                    .filter(child => !child.hide)
                    .filter(child => this.isItemVisible(child))
                    .map(child => ({
                        label: child.libelle.replace('menu.', this.menu),
                        routerLink: child.path,
                        domaines: child.domaines,
                        roles: child.roles
                    }));

                return {
                    label: item.libelle?.startsWith('menu.')
                        ? item.libelle.replace('menu.', this.menu)
                        : item.libelle,
                    icon: item.icon,
                    routerLink: children.length ? undefined : item.path,
                    domaines: item.domaines,
                    roles: item.roles,
                    items: children.length ? children : undefined
                };
            });
    }

    /**
     * Check if an item is visible for a domaine
     * @param item - Item to check
     * @private
     */
    private isItemVisible(item: any): boolean {
        const domaines: string[] | undefined = item?.domaines;
        if (domaines?.length) {
           
            if (!this.domaine || !domaines.includes(this.domaine)) {
                return false;
            }
        }

        return this.authService.hasRole(item?.roles);
    }

    /**
     * Navigate to a menu item
     * @param item - Item to navigate to
     */
    /**
     * Toggle l'ouverture/fermeture du submenu
     */
    toggleSubmenu(): void {
        this.submenuOpen = !this.submenuOpen;
    }

    /**
     * Naviguer vers un item du menu
     * @param item - Item to navigate to
     */
    navigate(item: MenuItem): void {

        if (item.items?.length) {
            if (this.activeItem === item && this.submenuOpen) {
                this.submenuOpen = false;
                return;
            }

            this.activeItem = item;
            this.activeSubItem = undefined;
            this.submenuOpen = true;
            return;
        }

        if (item.routerLink) {
            this.activeItem = item;
            this.activeSubItem = undefined;
            this.submenuOpen = false;

            this.router.navigate([item.routerLink]);
        }
    }

    /**
     * Check if a path is active
     * @param path - Path to check
     * @private
     */
    private isUrlActive(path: string): boolean {
        return this.router.isActive(this.router.createUrlTree([path]), {
            paths: 'exact',
            queryParams: 'ignored',
            fragment: 'ignored',
            matrixParams: 'ignored'
        });
    }

    /**
     * Check if a menu item is active (url match)
     * @param routerLink - Router link of the menu item
     */
    isMenuItemActive(routerLink: string): boolean {
        const currentUrl = this.router.url.split('?')[0];
        const base = routerLink.replace(/:([^/]+)/g, '[^/]+');
        const regex = new RegExp(`^${base}(/.*)?$`);
        return regex.test(currentUrl);
    }

    /**
     * Convert a router link to a regex
     * Example: /home => /^\/home(\/.*)?$/
     * @param routerLink - Router link to convert
     * @private
     */
    private routerLinkToRegex(routerLink: string): RegExp {
        const escapedRouterLink = routerLink.replace(/\//g, '\\/').replace(/:([^/]+)/g, '[^/]+');
        return new RegExp(`^${escapedRouterLink}$`);
    }

    /**
     * Check if a router link matches a current url
     * @param routerLink - Router link to check
     * @param currentUrl - Current url to check
     * @private
     */
    private matchesRouterLink(routerLink: string, currentUrl: string): boolean {
        return this.routerLinkToRegex(routerLink).test(currentUrl);
    }

    /**
     * Sync active item from url
     * @private
     */
    private syncActiveFromUrl(): void {
        const currentUrl = this.router.url.split('?')[0];

        for (const parent of this.items ?? []) {
            // Cas parent sans enfants
            if (parent.routerLink && this.isMenuItemActive(parent.routerLink)) {
                this.activeItem = parent;
                this.activeSubItem = undefined;
                this.submenuOpen = false;
                return;
            }

            // Cas parent avec enfants
            const activeChild = parent.items?.find(child => {
                if (!child.routerLink) {
                    return false;
                }

                const childPath = this.buildRouterLink(child.routerLink as string);
                return this.isMenuItemActive(childPath);
            });

            if (activeChild) {
                this.activeItem = parent;
                this.activeSubItem = activeChild;
                this.submenuOpen = true;
                return;
            }
        }

        this.activeItem = undefined;
        this.activeSubItem = undefined;
    }

    private getCurrentIdOperateur(): string | null {
        const findInSnapshot = (snapshot: any): string | null => {
            if (snapshot?.paramMap?.has('idOperateur')) {
                return snapshot.paramMap.get('idOperateur');
            }

            for (const child of snapshot?.children ?? []) {
                const found = findInSnapshot(child);
                if (found) {
                    return found;
                }
            }

            return null;
        };

        return findInSnapshot(this.router.routerState.snapshot.root);
    }

    buildRouterLink(path: string): string {
        if (!path) {
            return path;
        }

        const idOperateur = this.getCurrentIdOperateur();
        return idOperateur ? path.replace(':idOperateur', idOperateur) : path;
    }
}
